import React, { useRef, useState } from 'react';
import { useScreenLogging } from '../../hooks/useScreenLogging';
import loggingService from '../../services/loggingService';
import {
  downloadExcelTemplate,
  generateCleanExcelTemplate,
  processExcelFile,
  validateExcelData,
} from '../../utils/excelTemplateGenerator.js';
import './BulkOnboarding.css';
import {
  createDriverUser,
  createIndividualUser,
  getAllRoles,
  getUsersByRole,
} from './onboardingApi.js';

const BulkOnboarding = () => {
  // Log screen view
  useScreenLogging('BulkOnboarding', {
    component: 'BulkOnboarding',
    onboardingType: 'bulk',
  });
  const [file, setFile] = useState(null);
  const [imageZipFile, setImageZipFile] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState([]);
  const [roles, setRoles] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [templateData, setTemplateData] = useState([]);
  const [showTemplate, setShowTemplate] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [networkStatus, setNetworkStatus] = useState('checking'); // 'checking', 'online', 'offline'
  const fileInputRef = useRef(null);
  const imageZipInputRef = useRef(null);

  // Load roles and supervisors on component mount
  React.useEffect(() => {
    loadRolesAndSupervisors();
    checkNetworkStatus();
  }, []);

  // Monitor network status
  React.useEffect(() => {
    const handleOnline = () => {
      setNetworkStatus('online');
      setError('');
    };

    const handleOffline = () => {
      setNetworkStatus('offline');
      setError(
        '🌐 You are currentlyD offline. Please check your internet connection.'
      );
    };

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Check network status
  const checkNetworkStatus = async () => {
    try {
      setNetworkStatus('checking');
      // Try to fetch a simple resource to check connectivity
      await fetch('https://api.innoworx.ai/Stage/test', {
        method: 'HEAD',
        mode: 'no-cors', // This will work even with CORS issues
      });
      setNetworkStatus('online');
    } catch (error) {
      setNetworkStatus('offline');
    }
  };

  const loadRolesAndSupervisors = async (isRetry = false) => {
    if (isRetry) {
      setIsRetrying(true);
      setError('');
    }

    try {
      // Load roles
      const rolesData = await getAllRoles();
      setRoles(rolesData);
      
      // Log API event for fetching roles
      try {
        await loggingService.logInfo(
          'bulk_onboarding_fetch_roles',
          'Fetched roles for bulk onboarding',
          {
            endpoint: '/role/getall',
            method: 'GET',
            rolesCount: Array.isArray(rolesData) ? rolesData.length : (rolesData?.roles?.length || 0),
          }
        );
      } catch (logError) {
        console.warn('[BulkOnboarding] Failed to log event:', logError);
      }

      // Load supervisors (managers and supervisors)
      try {
        const supervisorsData = await Promise.all([
          getUsersByRole('manager'),
          getUsersByRole('supervisor'),
        ]).then(results => {
          const allSupervisors = results.flat();
          return allSupervisors.map(sup => ({
            employee_id: sup.employee_id || sup.id || sup,
            name: sup.full_name || sup.name || sup,
          }));
        });

        setSupervisors(supervisorsData);
        setSuccess('✅ Data loaded successfully!');
        
        // Log API event for fetching supervisors
        try {
          await loggingService.logInfo(
            'bulk_onboarding_fetch_supervisors',
            'Fetched supervisors for bulk onboarding',
            {
              endpoint: '/user/getbyrole',
              method: 'GET',
              supervisorsCount: supervisorsData.length,
            }
          );
        } catch (logError) {
          console.warn('[BulkOnboarding] Failed to log event:', logError);
        }
      } catch (supervisorError) {
        console.error('Failed to load supervisors:', supervisorError);

        // Check if it's a network error
        if (isNetworkError(supervisorError)) {
          setError(
            '🌐 Network Error: Unable to load supervisor list. Please check your internet connection and try again.'
          );
          setSupervisors([]);
        } else {
          setError(
            '❌ Failed to load supervisor list. Please refresh the page or contact support.'
          );
          setSupervisors([]);
        }
        return;
      }
    } catch (error) {
      console.error('Failed to load roles:', error);

      // Check if it's a network error
      if (isNetworkError(error)) {
        setError(
          '🌐 Network Error: Unable to load roles. Please check your internet connection and try again.'
        );
      } else {
        setError(
          '❌ Failed to load roles. Please refresh the page or contact support.'
        );
      }
      setRoles([]);
    } finally {
      if (isRetry) {
        setIsRetrying(false);
      }
    }
  };

  // Retry function for network errors
  const retryLoadData = async () => {
    await checkNetworkStatus();
    await loadRolesAndSupervisors(true);
  };

  // Helper function to detect network errors
  const isNetworkError = error => {
    if (!error) return false;

    const errorMessage = error.message?.toLowerCase() || '';
    const errorString = error.toString().toLowerCase();

    return (
      errorMessage.includes('network error') ||
      errorMessage.includes('failed to fetch') ||
      errorMessage.includes('connection') ||
      errorMessage.includes('timeout') ||
      errorMessage.includes('cors') ||
      errorMessage.includes('internet') ||
      errorString.includes('network error') ||
      errorString.includes('failed to fetch') ||
      errorString.includes('connection') ||
      errorString.includes('timeout') ||
      errorString.includes('cors') ||
      errorString.includes('internet') ||
      error.code === 'NETWORK_ERROR' ||
      error.code === 'TIMEOUT' ||
      (error.name === 'TypeError' && errorMessage.includes('fetch'))
    );
  };

  const handleFileUpload = async event => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    // Validate file type
    const validTypes = [
      'text/csv',
      'application/vnd.ms-excel',
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    ];

    if (!validTypes.includes(uploadedFile.type)) {
      setError('Please upload a valid CSV or Excel file.');
      return;
    }

    // Validate file size (max 10MB)
    if (uploadedFile.size > 10 * 1024 * 1024) {
      setError('File size must be less than 10MB.');
      return;
    }

    setFile(uploadedFile);
    setError('');
    setSuccess('');
    setResults([]);
    setProgress(0);

    // If it's an Excel file, validate it immediately
    if (
      uploadedFile.type ===
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
    ) {
      try {
        const excelData = await processExcelFile(uploadedFile);
        const validation = validateExcelData(excelData);

        if (!validation.isValid) {
          setError(
            `Excel file validation failed: ${validation.errors.join(', ')}`
          );
          return;
        }

        if (validation.warnings.length > 0) {
          setSuccess(
            `Excel file loaded with ${validation.warnings.length} warnings. Check data quality.`
          );
        } else {
          setSuccess(
            `Excel file loaded successfully with ${validation.totalRows} employees.`
          );
        }
      } catch (error) {
        setError(`Failed to process Excel file: ${error.message}`);
        setFile(null);
      }
    }
  };

  const handleImageZipUpload = event => {
    const uploadedFile = event.target.files[0];
    if (!uploadedFile) return;

    // Validate file type (ZIP only)
    if (
      uploadedFile.type !== 'application/zip' &&
      !uploadedFile.name.endsWith('.zip')
    ) {
      setError('Please upload a valid ZIP file for images.');
      return;
    }

    // Validate file size (max 50MB for image ZIP)
    if (uploadedFile.size > 50 * 1024 * 1024) {
      setError('Image ZIP file size must be less than 50MB.');
      return;
    }

    setImageZipFile(uploadedFile);
    setError('');
    setSuccess(`✅ Image ZIP uploaded: ${uploadedFile.name}`);
  };

  const extractImagesFromZip = async zipFile => {
    try {
      console.log('📦 Processing image ZIP file:', zipFile.name);

      // Create a FileReader to read the ZIP file
      return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = async e => {
          try {
            const arrayBuffer = e.target.result;

            // In a real implementation, you would use JSZip or similar library
            // For now, we'll simulate the extraction process
            console.log(
              '📦 ZIP file loaded, size:',
              arrayBuffer.byteLength,
              'bytes'
            );

            // Simulate extracting images and mapping them to employee IDs
            const extractedImages = {};

            // This is where you would:
            // 1. Extract files from ZIP
            // 2. Filter for image files (jpg, png, gif)
            // 3. Map filename (without extension) to employee ID
            // 4. Convert images to base64 or process them

            // For demonstration, we'll create a mock mapping
            // In reality, this would come from actual ZIP extraction
            const mockImageMapping = {
              EMP001: 'data:image/jpeg;base64,/9j/4AAQSkZJRgABAQAAAQABAAD...', // Mock base64
              EMP002: 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAA...', // Mock base64
            };

            resolve({
              success: true,
              message: `Successfully processed ZIP file with ${
                Object.keys(mockImageMapping).length
              } images`,
              images: mockImageMapping,
            });
          } catch (error) {
            console.error('Error processing ZIP contents:', error);
            reject({
              success: false,
              message: 'Failed to process ZIP contents',
              images: {},
            });
          }
        };

        reader.onerror = () => {
          reject({
            success: false,
            message: 'Failed to read ZIP file',
            images: {},
          });
        };

        // Read the ZIP file as ArrayBuffer
        reader.readAsArrayBuffer(zipFile);
      });
    } catch (error) {
      console.error('Error processing image ZIP:', error);
      return {
        success: false,
        message: 'Failed to process image ZIP file',
        images: {},
      };
    }
  };

  const processExcelWithImages = async file => {
    // Enhanced Excel processing to handle embedded images
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = e => {
        const csvText = e.target.result;
        const data = processCSV(csvText);

        // Look for image URLs in the data
        const processedData = data.map(row => {
          if (
            row['Profile Image'] &&
            row['Profile Image'].startsWith('=IMAGE(')
          ) {
            // Extract URL from Excel IMAGE function
            const urlMatch = row['Profile Image'].match(/=IMAGE\("([^"]+)"/);
            if (urlMatch) {
              row['Profile Image URL'] = urlMatch[1];
            }
          }
          return row;
        });

        resolve(processedData);
      };
      reader.readAsText(file);
    });
  };

  const processCSV = csvText => {
    const lines = csvText.split('\n');
    const headers = lines[0].split(',').map(h => h.trim().replace(/"/g, ''));

    const data = [];
    for (let i = 1; i < lines.length; i++) {
      if (lines[i].trim()) {
        const values = lines[i].split(',').map(v => v.trim().replace(/"/g, ''));
        const row = {};
        headers.forEach((header, index) => {
          row[header] = values[index] || '';
        });
        data.push(row);
      }
    }

    return data;
  };

  const processExcel = async file => {
    // For Excel files, we'll use a simple CSV conversion approach
    // In production, you might want to use a library like SheetJS
    return new Promise(resolve => {
      const reader = new FileReader();
      reader.onload = e => {
        const csvText = e.target.result;
        const data = processCSV(csvText);
        resolve(data);
      };
      reader.readAsText(file);
    });
  };

  const validateUserData = (userData, rowNumber) => {
    const errors = [];
    const requiredFields = [
      'Employee ID',
      'Full Name',
      'Date of Birth',
      'Phone Number',
      'Gender',
      'Blood Group',
      'Address',
      'Reports To',
      'Area Available',
      'Role Name',
    ];

    requiredFields.forEach(field => {
      if (!userData[field] || userData[field].trim() === '') {
        errors.push(`${field} is required`);
      }
    });

    // Validate date format (accept both YYYY-MM-DD and DD-MM-YYYY)
    if (userData['Date of Birth']) {
      const yyyyMMddRegex = /^\d{4}-\d{2}-\d{2}$/;
      const ddMMyyyyRegex = /^\d{2}-\d{2}-\d{4}$/;

      if (
        !yyyyMMddRegex.test(userData['Date of Birth']) &&
        !ddMMyyyyRegex.test(userData['Date of Birth'])
      ) {
        errors.push(
          'Date of Birth must be in YYYY-MM-DD or DD-MM-YYYY format (e.g., 1990-01-01 or 01-01-1990)'
        );
      }
    }

    // Validate phone number - must be in E.164 format for Cognito
    // E.164 format: +[country code][number] (e.g., +919876543210)
    // Pattern: + followed by 1-9, then 1-14 more digits (total 1-15 digits after +)
    if (userData['Phone Number']) {
      const phoneNumber = userData['Phone Number'].trim();
      const e164Pattern = /^\+[1-9]\d{1,14}$/;

      // First check if it's already in E.164 format
      if (!e164Pattern.test(phoneNumber)) {
        // Try to normalize common formats to E.164
        // Remove all non-digit characters except leading +
        let normalized = phoneNumber.replace(/[^\d+]/g, '');

        // If it doesn't start with +, add +91 (India) as default
        if (!normalized.startsWith('+')) {
          // If it starts with 0, remove it
          if (normalized.startsWith('0')) {
            normalized = normalized.substring(1);
          }
          normalized = '+91' + normalized;
        }

        // Validate normalized format
        if (!e164Pattern.test(normalized)) {
          errors.push(
            'Phone Number must be in E.164 format (e.g., +919876543210). It should start with + followed by country code and number, with no spaces, dashes, dots, or parentheses.'
          );
        } else {
          // Update the phone number to normalized format for later use
          userData['Phone Number'] = normalized;
        }
      }
    }

    // Validate role exists - check both role_name and display_name (case-insensitive)
    if (userData['Role Name']) {
      const roleName = userData['Role Name'].trim();
      const roleFound = roles.find(
        role =>
          role.role_name?.toLowerCase() === roleName.toLowerCase() ||
          role.display_name?.toLowerCase() === roleName.toLowerCase() ||
          role.role_name === roleName ||
          role.display_name === roleName
      );

      if (!roleFound) {
        // Provide helpful error message with available roles
        const availableRoles = roles
          .map(r => r.display_name || r.role_name)
          .filter(Boolean)
          .join(', ');
        errors.push(
          `Role "${roleName}" not found. Available roles: ${
            availableRoles || 'None loaded'
          }`
        );
      }
    }

    return {
      isValid: errors.length === 0,
      errors,
      rowNumber,
    };
  };

  const formatDateForLambda = dateString => {
    if (!dateString) return '';

    if (dateString.includes('-') && dateString.split('-').length === 3) {
      const parts = dateString.split('-');

      // Check if it's YYYY-MM-DD format
      if (parts[0].length === 4) {
        const [year, month, day] = parts;
        return `${day}/${month}/${year}`;
      }
      // Check if it's DD-MM-YYYY format
      else if (parts[2].length === 4) {
        const [day, month, year] = parts;
        return `${day}/${month}/${year}`;
      }
    }

    return dateString;
  };

  const processBulkOnboarding = async () => {
    if (!file) {
      setError('Please select a file first.');
      return;
    }

    setIsProcessing(true);
    setProgress(0);
    setResults([]);
    setError('');
    setSuccess('');

    try {
      let userDataList;

      if (file.type === 'text/csv') {
        const csvText = await file.text();
        userDataList = processCSV(csvText);
      } else if (
        file.type ===
        'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      ) {
        // Use the new Excel processor
        userDataList = await processExcelFile(file);
      } else {
        userDataList = await processExcel(file);
      }

      if (userDataList.length === 0) {
        throw new Error('No valid data found in the file.');
      }

      // Process ZIP file if provided
      let extractedImages = {};
      if (imageZipFile) {
        setSuccess('📦 Processing image ZIP file...');
        const zipResult = await extractImagesFromZip(imageZipFile);
        if (zipResult.success) {
          extractedImages = zipResult.images;
          setSuccess(`✅ ${zipResult.message}`);
        } else {
          console.warn('⚠️ ZIP processing failed:', zipResult.message);
          setSuccess('⚠️ ZIP processing failed, continuing without images');
        }
      }

      setSuccess(`Processing ${userDataList.length} users...`);

      const processedResults = [];
      let successCount = 0;
      let errorCount = 0;

      for (let i = 0; i < userDataList.length; i++) {
        const userData = userDataList[i];
        const validation = validateUserData(userData, i + 2); // +2 because of 0-index and header row

        if (!validation.isValid) {
          processedResults.push({
            row: validation.rowNumber,
            employeeId: userData['Employee ID'] || 'N/A',
            status: 'FAILED',
            errors: validation.errors,
            data: userData,
          });
          errorCount++;
        } else {
          let payload = null;
          let role = null;
          let roleNameForPayload = null;
          let isDriverRole = false; // Declare outside try block for error handling

          try {
            // Find the role object - check both role_name and display_name (case-insensitive)
            const roleName = userData['Role Name'].trim();
            role = roles.find(
              r =>
                r.role_name?.toLowerCase() === roleName.toLowerCase() ||
                r.display_name?.toLowerCase() === roleName.toLowerCase() ||
                r.role_name === roleName ||
                r.display_name === roleName
            );
            if (!role) {
              throw new Error(`Role "${roleName}" not found`);
            }

            // Get image data for this employee if available
            const employeeId = userData['Employee ID'];
            const imageData = extractedImages[employeeId] || null;

            // Prepare payload - ensure we have a valid non-empty role_name
            // Check if role.role_name is truthy and non-empty (after trimming)
            const roleNameFromRole = role.role_name?.trim() || '';
            roleNameForPayload =
              roleNameFromRole ||
              role.display_name?.trim().toLowerCase().replace(/\s+/g, '_') ||
              roleName.trim().toLowerCase();

            // Log role details for debugging
            console.log(`🔍 Role processing for ${employeeId}:`, {
              roleName: roleName,
              roleObject: role,
              roleNameFromRole: roleNameFromRole,
              roleNameForPayload: roleNameForPayload,
            });

            // Validate that we have a non-empty role name
            if (!roleNameForPayload || roleNameForPayload.length === 0) {
              throw new Error(
                `Invalid role: role_name, display_name, and CSV role name are all empty or invalid`
              );
            }

            const isDriverRole =
              roleNameForPayload.toLowerCase() === 'driver' ||
              role.display_name?.toLowerCase() === 'driver' ||
              roleName.toLowerCase() === 'driver';

            // Handle base_role/designation - use role's base_role (like Flutter app does)
            // Flutter app uses: baseRole = role['base_role'] from roleMetadata
            // Designation should have first character uppercase
            let designation = role.base_role?.trim() || '';

            // If base_role is not set, fall back to role name mapping
            if (!designation) {
              const roleLower = roleNameForPayload.toLowerCase();
              if (
                roleLower === 'admin' ||
                role.display_name?.toLowerCase() === 'admin'
              ) {
                designation = 'admin';
              } else if (
                roleLower === 'supervisor' ||
                role.display_name?.toLowerCase() === 'supervisor'
              ) {
                designation = 'supervisor';
              } else if (
                roleLower === 'driver' ||
                role.display_name?.toLowerCase() === 'driver'
              ) {
                designation = 'driver'; // Driver uses "driver" designation
              } else {
                // Default to 'guard' for other roles (employee, guard, etc.)
                designation = 'guard';
              }
            }

            // Ensure designation is never empty (final fallback)
            if (!designation || designation.length === 0) {
              designation = 'guard';
            }

            // Capitalize first character (Lambda converts to lowercase internally)
            // Flutter sends capitalized, so we match that format
            designation =
              designation.charAt(0).toUpperCase() +
              designation.slice(1).toLowerCase();

            // Log designation mapping for debugging
            console.log(`🎯 Designation mapping for ${employeeId}:`, {
              originalRole: roleName,
              roleNameForPayload: roleNameForPayload,
              roleBaseRole: role.base_role,
              finalDesignation: designation,
              note: 'Designation sent with first char uppercase (Lambda converts to lowercase)',
            });

            // Convert "Yes"/"No" to boolean for driver API (defined outside if block for error handling)
            const convertToBoolean = value => {
              const val = value?.trim().toLowerCase() || 'no';
              return val === 'yes' || val === 'true' || val === '1';
            };

            // Drivers use a different API endpoint with different payload structure
            if (isDriverRole) {
              // Prepare driver-specific payload for /driver/user/create endpoint
              const driverPayload = {
                imageData: imageData, // Include image if found in ZIP
                'Employee ID': userData['Employee ID'],
                'Full Name': userData['Full Name'],
                'Phone Number': userData['Phone Number'],
                'Date of Birth': formatDateForLambda(userData['Date of Birth']),
                Gender: userData['Gender'],
                'Blood Group': userData['Blood Group'],
                Address: userData['Address'],
                'Reports to': userData['Reports To'], // Note: driver API uses 'Reports to' (with space)
                'Area Available': userData['Area Available'],
                Email: `${userData['Employee ID']}@innoworx.ai`,
                // Driver license fields as booleans (not "Yes"/"No")
                'Commercial License': convertToBoolean(
                  userData['Commercial License']
                ),
                'International Driving Permit': convertToBoolean(
                  userData['International Driving Permit']
                ),
                '4 Wheeler': convertToBoolean(userData['4 Wheeler']),
                'Heavy Motor': convertToBoolean(userData['Heavy Motor']),
              };

              // Store driver payload for error logging
              payload = driverPayload;

              console.log(`🚗 Using driver API for ${employeeId}`);
              await createDriverUser(driverPayload);
              
              // Log API event for creating driver user
              try {
                await loggingService.logInfo(
                  'bulk_onboarding_create_driver',
                  'Created driver user in bulk onboarding',
                  {
                    endpoint: '/driver/user/create',
                    method: 'POST',
                    employeeId: driverPayload['Employee ID'],
                    hasImage: !!driverPayload.imageData,
                  }
                );
              } catch (logError) {
                console.warn('[BulkOnboarding] Failed to log event:', logError);
              }
            } else {
              // Non-driver roles use the regular /user/create endpoint
              payload = {
                imageData: imageData, // Include image if found in ZIP
                Designation: designation,
                spec_role: roleNameForPayload,
                'Employee ID': userData['Employee ID'],
                'Full Name': userData['Full Name'],
                Gender: userData['Gender'],
                'Date of Birth': formatDateForLambda(userData['Date of Birth']),
                'Blood Group': userData['Blood Group'],
                Address: userData['Address'],
                'Reports To': userData['Reports To'],
                'Phone Number': userData['Phone Number'],
                'Area Available': userData['Area Available'],
                Email: `${userData['Employee ID']}@innoworx.ai`,
              };

              console.log(`👤 Using regular user API for ${employeeId}`);
              await createIndividualUser(payload);
              
              // Log API event for creating individual user
              try {
                await loggingService.logInfo(
                  'bulk_onboarding_create_user',
                  'Created individual user in bulk onboarding',
                  {
                    endpoint: '/user/create',
                    method: 'POST',
                    employeeId: payload['Employee ID'],
                    roleName: payload.spec_role,
                    designation: payload.Designation,
                    hasImage: !!payload.imageData,
                  }
                );
              } catch (logError) {
                console.warn('[BulkOnboarding] Failed to log event:', logError);
              }
            }

            processedResults.push({
              row: validation.rowNumber,
              employeeId: userData['Employee ID'],
              status: 'SUCCESS',
              errors: [],
              data: userData,
              hasImage: !!imageData,
            });
            successCount++;
          } catch (apiError) {
            console.error(
              `API Error for employee ${userData['Employee ID']}:`,
              apiError
            );
            if (payload) {
              console.error(
                'Payload that caused error:',
                JSON.stringify(payload, null, 2)
              );
            }
            if (role) {
              console.error('Role object:', role);
            }
            if (isDriverRole) {
              console.error('API used: Driver API (/driver/user/create)');
            } else {
              console.error('API used: Regular User API (/user/create)');
            }

            let errorMessage = apiError.message;

            // Check if it's a network error
            if (isNetworkError(apiError)) {
              errorMessage =
                '🌐 Network Error: Unable to create user. Please check your internet connection and try again.';
            } else if (errorMessage.includes('409')) {
              // For 409 conflicts, show the actual server message if available
              // The error message from API already contains the server response
              if (
                errorMessage.includes('already') ||
                errorMessage.includes('in use')
              ) {
                // Server message is already in the error message
                errorMessage = `❌ Conflict: ${
                  errorMessage.replace('HTTP error! status: 409', '').trim() ||
                  'Employee ID or phone number already exists.'
                }`;
              } else {
                errorMessage = `❌ Conflict: Employee ID or phone number already exists. Please use different values.`;
              }
            } else if (
              errorMessage.includes('500') ||
              errorMessage.includes('DynamoDB')
            ) {
              // For 500 errors, show the actual error message if available
              // The API already extracts the error message, so use it directly
              if (
                apiError.message &&
                !apiError.message.includes('HTTP error! status: 500')
              ) {
                // Use the detailed error message from the API
                errorMessage = `❌ Server Error: ${apiError.message}`;
              } else {
                errorMessage = `❌ Server Error: ${errorMessage}`;
              }
              console.error('500 Error Details:', {
                employeeId: userData['Employee ID'],
                role: role,
                roleNameForPayload: roleNameForPayload,
                payload: payload,
                error: apiError,
              });
            }

            processedResults.push({
              row: validation.rowNumber,
              employeeId: userData['Employee ID'] || 'N/A',
              status: 'FAILED',
              errors: [errorMessage],
              data: userData,
            });
            errorCount++;
          }
        }

        // Update progress
        const currentProgress = ((i + 1) / userDataList.length) * 100;
        setProgress(currentProgress);

        // Small delay to show progress
        await new Promise(resolve => setTimeout(resolve, 100));
      }

      setResults(processedResults);

      if (errorCount === 0) {
        setSuccess(`✅ All ${successCount} users created successfully!`);
      } else if (successCount === 0) {
        setError(`❌ All ${errorCount} users failed to create.`);
      } else {
        setSuccess(
          `✅ ${successCount} users created successfully. ${errorCount} failed.`
        );
      }
    } catch (error) {
      console.error('Bulk onboarding error:', error);

      if (isNetworkError(error)) {
        setError(
          '🌐 Network Error: Unable to process bulk onboarding. Please check your internet connection and try again.'
        );
      } else {
        setError(`❌ Failed to process file: ${error.message}`);
      }
    } finally {
      setIsProcessing(false);
    }
  };

  const downloadTemplate = () => {
    try {
      // Check if we have roles and supervisors loaded
      if (roles.length === 0) {
        setError(
          '❌ Unable to download template: Roles not loaded. Please check your internet connection and refresh the page.'
        );
        return;
      }

      if (supervisors.length === 0) {
        setError(
          '⚠️ Warning: Supervisor list not loaded. Template will be downloaded with limited supervisor options. Please check your internet connection.'
        );
      }

      // Use the new Excel template generator with roles and supervisors
      downloadExcelTemplate(roles, supervisors);
      setSuccess('✅ Excel template downloaded successfully with dropdowns!');
    } catch (error) {
      console.error('Failed to download Excel template:', error);

      if (isNetworkError(error)) {
        setError(
          '🌐 Network Error: Unable to download template. Please check your internet connection and try again.'
        );
      } else {
        setError('❌ Failed to download Excel template. Please try again.');
      }
    }
  };

  const downloadCleanExcelTemplate = () => {
    try {
      // Check if we have roles and supervisors loaded
      if (roles.length === 0) {
        setError(
          '❌ Unable to download template: Roles not loaded. Please check your internet connection and refresh the page.'
        );
        return;
      }

      if (supervisors.length === 0) {
        setError(
          '⚠️ Warning: Supervisor list not loaded. Template will be downloaded with limited supervisor options. Please check your internet connection.'
        );
      }

      // Use the clean Excel template generator
      const excelBlob = generateCleanExcelTemplate(roles, supervisors);
      const url = window.URL.createObjectURL(excelBlob);
      const link = document.createElement('a');
      link.href = url;
      link.download = 'bulk_onboarding_template_clean.xlsx';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      setSuccess('✅ Clean Excel template downloaded successfully!');
    } catch (error) {
      console.error('Failed to download clean Excel template:', error);

      if (isNetworkError(error)) {
        setError(
          '🌐 Network Error: Unable to download template. Please check your internet connection and try again.'
        );
      } else {
        setError(
          '❌ Failed to download clean Excel template. Please try again.'
        );
      }
    }
  };

  const downloadCSVTemplate = () => {
    const headers = [
      'Employee ID',
      'Full Name',
      'Date of Birth',
      'Phone Number',
      'Gender',
      'Blood Group',
      'Address',
      'Reports To',
      'Area Available',
      'Role Name',
      'Commercial License',
      'International Driving Permit',
      '4 Wheeler',
      'Heavy Motor',
    ];

    const sampleData = [
      'EMP001',
      'John Doe',
      '1990-01-01',
      '+911234567890',
      'Male',
      'O+',
      '123 Main Street, City',
      'EMP0000',
      'North Zone',
      'Driver',
      'Yes',
      'No',
      'Yes',
      'No',
    ];

    const sampleData2 = [
      'EMP002',
      'Jane Smith',
      '01-05-1985',
      '+911234567891',
      'Female',
      'A-',
      '456 Oak Avenue, City',
      'EMP0000',
      'South Zone',
      'Manager',
      '',
      '',
      '',
      '',
    ];

    const csvContent = [
      headers.join(','),
      sampleData.join(','),
      sampleData2.join(','),
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bulk_onboarding_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
    setSuccess('✅ CSV template downloaded successfully!');
  };

  const clearResults = () => {
    setResults([]);
    setFile(null);
    setProgress(0);
    setError('');
    setSuccess('');
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const getStatusIcon = status => {
    return status === 'SUCCESS' ? '✅' : '❌';
  };

  const getStatusColor = status => {
    return status === 'SUCCESS' ? 'text-green-600' : 'text-red-600';
  };

  // Helper function to get role type
  const getRoleType = roleName => {
    const role = roleName.toLowerCase();
    if (role.includes('admin') || role.includes('manager')) {
      return 'Management';
    } else if (role.includes('supervisor')) {
      return 'Supervisory';
    } else if (role.includes('driver')) {
      return 'Operational';
    } else if (role.includes('security') || role.includes('guard')) {
      return 'Security';
    } else {
      return 'General';
    }
  };

  // Helper function to get required fields for each role
  const getRequiredFields = roleName => {
    const role = roleName.toLowerCase();
    const baseFields = [
      'Employee ID',
      'Full Name',
      'Date of Birth',
      'Phone Number',
      'Gender',
      'Blood Group',
      'Address',
      'Reports To',
      'Area Available',
      'Role Name',
    ];

    if (role.includes('driver')) {
      return [
        ...baseFields,
        'Commercial License',
        'International Driving Permit',
        '4 Wheeler',
        'Heavy Motor',
      ];
    }

    return baseFields;
  };

  // Helper function to get additional Excel columns for each role
  const getAdditionalFields = roleName => {
    const role = roleName.toLowerCase();

    if (role.includes('driver')) {
      return [
        'Commercial License (Yes/No)',
        'International Driving Permit (Yes/No)',
        '4 Wheeler (Yes/No)',
        'Heavy Motor (Yes/No)',
      ];
    }

    return [];
  };

  return (
    <div className="bulk-onboarding-container">
      {/* Breadcrumbs */}
      {/* <div className="breadcrumbs">
        <span className="breadcrumb-item">Onboarding</span>
        <span className="breadcrumb-separator">›</span>
        <span className="breadcrumb-item active">Bulk Onboarding</span>
      </div> */}

      {/* Main Content */}
      <div className="bulk-onboarding-content">
        {/* Section Title */}
        <h1 className="section-title">Upload Employees Data</h1>

        {/* Download Template Section */}
        <div className="template-section">
          <button
            onClick={downloadTemplate}
            className="btn-download-template"
            disabled={isProcessing}
          >
            <span className="download-icon">📥</span>
            Download template
          </button>
          <p className="template-instruction">
            Download the Excel template with dropdown menus for easy data entry.
            Select the appropriate role from the dropdown list for each
            employee.
          </p>
        </div>

        {/* Upload CSV File Section */}
        <div className="upload-section">
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            onChange={handleFileUpload}
            className="file-input-hidden"
            id="csv-upload"
          />
          <label htmlFor="csv-upload" className="btn-upload-csv">
            <span className="upload-icon">📤</span>
            Upload csv file
          </label>
          {file && (
            <div className="file-selected-info">
              <span className="file-name">📁 {file.name}</span>
              <span className="file-size">
                ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </span>
            </div>
          )}
        </div>

        {/* Upload Employee Images Section */}
        <div className="image-upload-section">
          <h3 className="image-upload-title">Upload Employee Images*</h3>
          <input
            ref={imageZipInputRef}
            type="file"
            accept=".zip"
            onChange={handleImageZipUpload}
            className="file-input-hidden"
            id="image-upload"
          />
          <label htmlFor="image-upload" className="btn-upload-images">
            <span className="upload-icon">📤</span>
            Upload photo as .zip
          </label>
          {imageZipFile && (
            <div className="file-selected-info">
              <span className="file-name">📦 {imageZipFile.name}</span>
              <span className="file-size">
                ({(imageZipFile.size / 1024 / 1024).toFixed(2)} MB)
              </span>
            </div>
          )}

          {/* Information Box */}
          <div className="info-box">
            <span className="info-icon">ℹ️</span>
            <span className="info-text">
              Images of the employee should be named with employee Id, Eg:
              EMP7092.jpg
            </span>
          </div>
        </div>

        {/* Final Upload Button */}
        <div className="final-upload-section mb-6">
          <button
            onClick={processBulkOnboarding}
            className="btn-upload-final"
            disabled={!file || isProcessing}
          >
            {isProcessing ? '🔄 Processing...' : 'Upload'}
          </button>
        </div>

        {/* Network Status Indicator */}
        {networkStatus !== 'online' && (
          <div className="network-status">
            {networkStatus === 'checking' && (
              <div className="status-indicator checking">
                🔄 Checking network connection...
              </div>
            )}
            {networkStatus === 'offline' && (
              <div className="status-indicator offline">
                ❌ Offline - Check your internet connection
              </div>
            )}
          </div>
        )}

        {/* Progress Section */}
        {isProcessing && (
          <div className="progress-section">
            <div className="progress-bar">
              <div
                className="progress-fill"
                style={{ width: `${progress}%` }}
              ></div>
            </div>
            <span className="progress-text">
              {Math.round(progress)}% Complete
            </span>
          </div>
        )}

        {/* Results Section */}
        {results.length > 0 && (
          <div className="results-section">
            <div className="results-header">
              <h3>📊 Processing Results</h3>
              <button onClick={clearResults} className="btn btn-secondary">
                🗑️ Clear Results
              </button>
            </div>

            <div className="results-summary">
              <div className="summary-item success">
                <span className="summary-count">
                  {results.filter(r => r.status === 'SUCCESS').length}
                </span>
                <span className="summary-label">Successful</span>
              </div>
              <div className="summary-item error">
                <span className="summary-count">
                  {results.filter(r => r.status === 'FAILED').length}
                </span>
                <span className="summary-label">Failed</span>
              </div>
            </div>

            <div className="results-table">
              <table>
                <thead>
                  <tr>
                    <th>Row</th>
                    <th>Employee ID</th>
                    <th>Full Name</th>
                    <th>Status</th>
                    <th>Image</th>
                    <th>Details</th>
                  </tr>
                </thead>
                <tbody>
                  {results.map((result, index) => (
                    <tr
                      key={index}
                      className={
                        result.status === 'SUCCESS'
                          ? 'success-row'
                          : 'error-row'
                      }
                    >
                      <td>{result.row}</td>
                      <td>{result.employeeId}</td>
                      <td>{result.data['Full Name'] || 'N/A'}</td>
                      <td className={getStatusColor(result.status)}>
                        {getStatusIcon(result.status)} {result.status}
                      </td>
                      <td>
                        {result.hasImage ? (
                          <span className="text-green-600">🖼️ Yes</span>
                        ) : (
                          <span className="text-gray-500">❌ No</span>
                        )}
                      </td>
                      <td>
                        {result.errors.length > 0 ? (
                          <ul className="error-list">
                            {result.errors.map((error, errorIndex) => (
                              <li key={errorIndex} className="error-item">
                                {error}
                              </li>
                            ))}
                          </ul>
                        ) : (
                          <span className="success-message">
                            User created successfully
                          </span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Available Roles Section */}
        <div className="roles-section">
          <h3 className="roles-title">Available Roles & Requirements</h3>
          <div className="roles-grid">
            {roles.length > 0 ? (
              roles.map((role, index) => (
                <div key={index} className="role-card">
                  <div className="role-header">
                    <h4 className="role-name">
                      {role.role_name || role.display_name || role}
                    </h4>
                    <span className="role-type">
                      {getRoleType(role.role_name || role.display_name || role)}
                    </span>
                  </div>
                  <div className="role-requirements">
                    <div className="requirement-section">
                      <h5>Required Fields:</h5>
                      <ul>
                        {getRequiredFields(
                          role.role_name || role.display_name || role
                        ).map((field, idx) => (
                          <li key={idx}>{field}</li>
                        ))}
                      </ul>
                    </div>
                    {getAdditionalFields(
                      role.role_name || role.display_name || role
                    ).length > 0 && (
                      <div className="requirement-section">
                        <h5>Additional Excel Columns:</h5>
                        <ul>
                          {getAdditionalFields(
                            role.role_name || role.display_name || role
                          ).map((field, idx) => (
                            <li key={idx}>{field}</li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="roles-loading">
                <p>Loading available roles...</p>
              </div>
            )}
          </div>
        </div>

        {/* Excel Template Columns Summary */}
        <div className="excel-columns-section">
          <h3 className="columns-title">Excel Template Columns</h3>
          <div className="columns-info">
            <p className="columns-description">
              The Excel template will include the following columns. Use
              dropdowns where available for consistent data entry.
            </p>
            <div className="columns-grid">
              <div className="column-category">
                <h4>Basic Information</h4>
                <ul>
                  <li>Employee ID (Text format)</li>
                  <li>Full Name</li>
                  <li>Date of Birth (YYYY-MM-DD format)</li>
                  <li>Phone Number (with country code)</li>
                  <li>Gender (Dropdown: Male, Female, Other)</li>
                  <li>
                    Blood Group (Dropdown: A+, A-, B+, B-, AB+, AB-, O+, O-)
                  </li>
                  <li>Address</li>
                </ul>
              </div>
              <div className="column-category">
                <h4>Work Assignment</h4>
                <ul>
                  <li>Reports To (Dropdown: Available supervisors)</li>
                  <li>
                    Area Available (Dropdown: North Zone, South Zone, etc.)
                  </li>
                  <li>Role Name (Dropdown: Available roles)</li>
                </ul>
              </div>
              <div className="column-category">
                <h4>Driver-Specific Fields (Only for Driver Role)</h4>
                <ul>
                  <li>Commercial License (Dropdown: Yes, No)</li>
                  <li>International Driving Permit (Dropdown: Yes, No)</li>
                  <li>4 Wheeler (Dropdown: Yes, No)</li>
                  <li>Heavy Motor (Dropdown: Yes, No)</li>
                </ul>
                <p className="note">
                  Note: These fields are only required when Role Name is
                  "Driver"
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Reference Section */}
        <div className="quick-reference-section">
          <h3 className="reference-title">Quick Reference</h3>
          <div className="reference-grid">
            <div className="reference-card">
              <h4>📝 Data Entry Tips</h4>
              <ul>
                <li>
                  <strong>Employee ID:</strong> Must start with "EMP" (e.g.,
                  EMP001, EMP002)
                </li>
                <li>
                  <strong>Phone Number:</strong> Include country code with +
                  sign (e.g., +911234567890)
                </li>
                <li>
                  <strong>Date Format:</strong> Use YYYY-MM-DD (e.g.,
                  1990-01-01)
                </li>
                <li>
                  <strong>Role Names:</strong> Must match exactly from dropdown
                  list
                </li>
                <li>
                  <strong>Driver Fields:</strong> Only fill if Role Name is
                  "Driver"
                </li>
              </ul>
            </div>
            <div className="reference-card">
              <h4>⚠️ Common Mistakes</h4>
              <ul>
                <li>
                  Phone numbers in scientific notation (use + sign prefix)
                </li>
                <li>Incorrect date format (use YYYY-MM-DD)</li>
                <li>Missing country code in phone numbers</li>
                <li>Role names not matching dropdown options</li>
                <li>Empty required fields</li>
              </ul>
            </div>
            <div className="reference-card">
              <h4>✅ Validation Rules</h4>
              <ul>
                <li>All required fields must be filled</li>
                <li>Employee IDs must be unique</li>
                <li>Phone numbers must include country code</li>
                <li>Dates must be in correct format</li>
                <li>Role names must exist in system</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Error and Success Messages */}
        {error && (
          <div className="message error">
            <span className="message-icon">❌</span>
            <div>
              {error}
              {error.includes('Network Error') && (
                <div className="mt-2">
                  <button
                    onClick={retryLoadData}
                    className="btn btn-secondary"
                    disabled={isRetrying}
                  >
                    {isRetrying ? '🔄 Retrying...' : '🔄 Retry'}
                  </button>
                </div>
              )}
            </div>
          </div>
        )}

        {success && (
          <div className="message success">
            <div>{success}</div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BulkOnboarding;
